import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Mail, FileText, Calendar, CheckCircle, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../utils/api';

interface Notification {
  id: number;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
  data: any;
}

interface NotificationDropdownProps {
  onMarkAllRead: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onMarkAllRead }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('skillseek_admin_token');
      
      const response = await fetch(`${API_BASE_URL}/notifications/admin?limit=5`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNotifications(data.notifications);
        setUnreadCount(data.stats.unread);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('skillseek_admin_token');
      
      const response = await fetch(`${API_BASE_URL}/notifications/admin/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUnreadCount(data.unreadCount.total);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (id: number) => {
    try {
      const token = localStorage.getItem('skillseek_admin_token');
      
      const response = await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === id 
              ? { ...notification, is_read: true } 
              : notification
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('skillseek_admin_token');
      
      const response = await fetch(`${API_BASE_URL}/notifications/admin/read-all`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, is_read: true }))
        );
        setUnreadCount(0);
        onMarkAllRead();
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 60000); // Every minute
    
    return () => clearInterval(interval);
  }, []);

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMs / 3600000);
    const diffDays = Math.round(diffMs / 86400000);
    
    if (diffMins < 60) {
      return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'booking':
        return <Calendar size={18} className="notification-icon booking" />;
      case 'contact':
        return <Mail size={18} className="notification-icon contact" />;
      case 'system':
        return <AlertTriangle size={18} className="notification-icon system" />;
      default:
        return <FileText size={18} className="notification-icon general" />;
    }
  };

  // Get notification link
  const getNotificationLink = (notification: Notification) => {
    if (!notification.data) return '#';
    
    try {
      const data = typeof notification.data === 'string' 
        ? JSON.parse(notification.data) 
        : notification.data;
      
      if (data.type === 'service_inquiry') {
        return '/admin/inquiries';
      }
      return '#';
    } catch (e) {
      return '#';
    }
  };

  return (
    <div className="shared-notification-dropdown" ref={dropdownRef}>
      <button 
        className="shared-notification-button" 
        onClick={toggleDropdown}
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
      
      {isOpen && (
        <div className="shared-notification-dropdown-content">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className="mark-all-read" 
                onClick={handleMarkAllRead}
              >
                <CheckCircle size={14} />
                Mark all as read
              </button>
            )}
          </div>
          
          <div className="notification-body">
            {loading ? (
              <div className="notification-loading">
                <div className="spinner"></div>
             
              </div>
            ) : notifications.length > 0 ? (
              notifications.map(notification => (
                <div 
                  key={notification.id} 
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                >
                  <Link to={getNotificationLink(notification)}>
                    <div className="notification-icon-wrapper">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="notification-content">
                      <h4>{notification.title}</h4>
                      <p>{notification.message}</p>
                      <span className="notification-time">{formatDate(notification.created_at)}</span>
                    </div>
                    {!notification.is_read && <div className="unread-indicator"></div>}
                  </Link>
                </div>
              ))
            ) : (
              <div className="notification-empty">
                <Bell size={32} />
                <p>No notifications yet</p>
              </div>
            )}
          </div>
          
          <div className="notification-footer">
            <Link to="/notifications" onClick={() => setIsOpen(false)}>
              View all notifications
            </Link>
          </div>
        </div>
      )}
      
      {/* CSS styles without jsx attribute */}
      <style>{`
        .shared-notification-dropdown {
          position: relative;
        }
        
        .shared-notification-button {
          background: none;
          border: none;
          color: #555;
          cursor: pointer;
          position: relative;
          padding: 8px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
        }
        
        .shared-notification-button:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }
        
        .notification-badge {
          position: absolute;
          top: 0;
          right: 0;
          background-color: #e74c3c;
          color: white;
          border-radius: 10px;
          padding: 2px 6px;
          font-size: 10px;
          font-weight: bold;
          min-width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .shared-notification-dropdown-content {
          position: absolute;
          right: 0;
          top: 100%;
          width: 360px;
          max-width: calc(100vw - 20px);
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          overflow: hidden;
          animation: slideDown 0.2s ease;
        }
        
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .notification-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          border-bottom: 1px solid #eee;
        }
        
        .notification-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
        }
        
        .mark-all-read {
          background: none;
          border: none;
          color: #4f46e5;
          font-size: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 5px 10px;
          border-radius: 4px;
          transition: background-color 0.2s;
        }
        
        .mark-all-read:hover {
          background-color: rgba(79, 70, 229, 0.1);
        }
        
        .notification-body {
          max-height: 350px;
          overflow-y: auto;
        }
        
        .notification-item {
          padding: 0;
          border-bottom: 1px solid #eee;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .notification-item:hover {
          background-color: #f9f9f9;
        }
        
        .notification-item.unread {
          background-color: #f0f7ff;
        }
        
        .notification-item.unread:hover {
          background-color: #e6f2ff;
        }
        
        .notification-item a {
          display: flex;
          padding: 15px;
          text-decoration: none;
          color: inherit;
          position: relative;
        }
        
        .notification-icon-wrapper {
          margin-right: 15px;
          display: flex;
          align-items: flex-start;
        }
        
        .notification-icon {
          padding: 8px;
          border-radius: 50%;
          background-color: #f0f0f0;
        }
        
        .notification-icon.booking {
          color: #4f46e5;
          background-color: rgba(79, 70, 229, 0.1);
        }
        
        .notification-icon.contact {
          color: #10b981;
          background-color: rgba(16, 185, 129, 0.1);
        }
        
        .notification-icon.system {
          color: #f59e0b;
          background-color: rgba(245, 158, 11, 0.1);
        }
        
        .notification-icon.general {
          color: #6b7280;
          background-color: rgba(107, 114, 128, 0.1);
        }
        
        .notification-content {
          flex: 1;
        }
        
        .notification-content h4 {
          margin: 0 0 5px;
          font-size: 14px;
          font-weight: 600;
        }
        
        .notification-content p {
          margin: 0 0 5px;
          font-size: 13px;
          color: #666;
          line-height: 1.4;
        }
        
        .notification-time {
          font-size: 11px;
          color: #888;
        }
        
        .unread-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #4f46e5;
          position: absolute;
          right: 15px;
          top: 15px;
        }
        
        .notification-footer {
          padding: 12px;
          text-align: center;
          border-top: 1px solid #eee;
        }
        
        .notification-footer a {
          color: #4f46e5;
          font-size: 13px;
          text-decoration: none;
          font-weight: 500;
        }
        
        .notification-footer a:hover {
          text-decoration: underline;
        }
        
        .notification-empty {
          padding: 30px;
          text-align: center;
          color: #888;
        }
        
        .notification-empty svg {
          opacity: 0.3;
          margin-bottom: 10px;
        }
        
        .notification-empty p {
          margin: 0;
          font-size: 14px;
        }
        
        .notification-loading {
          padding: 30px;
          text-align: center;
          color: #888;
        }
        
        .spinner {
          width: 30px;
          height: 30px;
          border: 3px solid rgba(0, 0, 0, 0.1);
          border-top-color: #4f46e5;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 10px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default NotificationDropdown;